import {
  Box,
  Input as ChakraInput,
  InputProps,
  ResponsiveValue,
  FormLabel,
} from '@chakra-ui/react';
import React, { ChangeEventHandler, forwardRef } from 'react';
import { format } from 'date-fns';
import { CustomDateTimePicker } from '../CustomDateTimePicker';

interface DatePickerInputProps {
  value: string;
  onClick?: () => void;
  placeholder?: string;
  size?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  variant?: string;
  backgroundColor?: string;
  height?: ResponsiveValue<string>;
  paddingLeft?: ResponsiveValue<string>;
  fontSize?: ResponsiveValue<string>;
  inputTextColor?: string;
  width?: ResponsiveValue<string>;
  textOverflow?: string;
  placeholderColor?: string;
}

const DatePickerInput = forwardRef<HTMLInputElement, DatePickerInputProps>(
  (
    {
      value,
      onClick,
      placeholder,
      size,
      isDisabled,
      isRequired,
      isInvalid,
      variant,
      backgroundColor,
      height,
      paddingLeft,
      fontSize,
      inputTextColor,
      width,
      textOverflow,
      placeholderColor,
    },
    ref,
  ) => (
    <ChakraInput
      ref={ref}
      value={value}
      onClick={onClick}
      placeholder={placeholder}
      size={size}
      isDisabled={isDisabled}
      isRequired={isRequired}
      isInvalid={isInvalid}
      variant={variant}
      backgroundColor={backgroundColor}
      height={height}
      paddingLeft={paddingLeft}
      fontSize={fontSize}
      color={inputTextColor}
      width={width}
      textOverflow={textOverflow}
      sx={{
        '::placeholder': {
          color: placeholderColor,
        },
      }}
    />
  ),
);

DatePickerInput.displayName = 'DatePickerInput'; // to avoid React warning

interface CustomInputProps extends InputProps {
  placeholder?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onDateTimeChange?: (date: Date | null) => void;
  dateTimeValue?: Date | null;
  size?: string;
  value?: string | number;
  type?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  variant?: string;
  bgColor?: string;
  height?: ResponsiveValue<string>;
  paddingLeft?: ResponsiveValue<string>;
  fontSize?: ResponsiveValue<string>;
  placeholderColor?: string;
  placeholderFontSize?: ResponsiveValue<string>;
  placeholderFontWeight?: string;
  inputTextColor?: string;
  isRequired?: boolean;
  label?: string;
  labelColor?: string;
  labelSize?: string;
  marginTop?: string;
  width?: ResponsiveValue<string>;
  textOverflow?: string;
  borderColor?: string;
  borderRadius?: string;
  borderWidth?: string;
}

export const CustomInput: React.FC<CustomInputProps> = ({
  label,
  labelColor,
  labelSize,
  placeholder,
  onChange,
  onDateTimeChange,
  size = 'md',
  value,
  dateTimeValue,
  type = 'text',
  isDisabled = false,
  isInvalid = false,
  variant = 'outline',
  bgColor = '#fff',
  height,
  paddingLeft,
  fontSize,
  placeholderColor = '#A0AEC0',
  placeholderFontSize,
  placeholderFontWeight,
  inputTextColor = '#000',
  width,
  textOverflow,
  marginTop,
  isRequired,
  borderColor,
  borderRadius,
  borderWidth,
}) => {
  const isDateTime = type === 'date-time';

  return (
    <Box width="100%" shadow="none" marginTop={marginTop}>
      {label && (
        <FormLabel color={labelColor} fontSize={labelSize} fontWeight="bold">
          {label}
        </FormLabel>
      )}

      {isDateTime ? (
        <CustomDateTimePicker
          label={undefined} // omit if already above
          placeholder={placeholder}
          value={dateTimeValue ? format(dateTimeValue, "yyyy-MM-dd'T'HH:mm") : ''}
          onChange={date => {
            onDateTimeChange?.(date);
          }}
          size={size}
          isDisabled={isDisabled}
          isRequired={isRequired}
          isInvalid={isInvalid}
          variant={variant}
          backgroundColor={bgColor}
          height={height}
          paddingLeft={paddingLeft}
          fontSize={fontSize}
          color={inputTextColor}
          width={width}
          textOverflow={textOverflow}
          placeholderColor={placeholderColor}
        />
      ) : (
        <ChakraInput
          placeholder={placeholder}
          onChange={onChange}
          size={size}
          value={value}
          type={type}
          isDisabled={isDisabled}
          isRequired={isRequired}
          isInvalid={isInvalid}
          variant={variant}
          backgroundColor={bgColor}
          height={height}
          paddingLeft={paddingLeft}
          fontSize={fontSize}
          color={inputTextColor}
          width={width}
          textOverflow={textOverflow}
          sx={{
            '::placeholder': {
              color: placeholderColor,
              fontSize: placeholderFontSize || 'inherit',
              fontWeight: placeholderFontWeight || 'normal',
            },
          }}
          borderColor={borderColor}
          borderRadius={borderRadius}
          borderWidth={borderWidth}
        />
      )}
    </Box>
  );
};
